import React, { useEffect, useState } from 'react'
import {
  Backdrop,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  InputAdornment,
  MenuItem,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { Add, Search, Extension } from '@mui/icons-material'
import { Helmet } from 'react-helmet'
import { useNavigate } from 'react-router-dom'
import { api } from '../../../../services/Main/Api'
import PaginationGlobal from '../../../../components/Shared/PaginationGlobal'
import NoResultsAnimation from '../../../../components/Shared/animations/NoResultsAnimation'
import moment from 'moment'

const FeatureList = () => {
  const navigate = useNavigate()

  const [response, setResponse] = useState(null)
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [filters, setFilters] = useState({ page: 1, page_size: 50 })

  const fetchData = async () => {
    setLoading(true)
    try {
      const params = {
        page: filters.page,
        page_size: filters.page_size,
        ...(search && { search }),
        ...(categoryFilter && { category: categoryFilter }),
        ...(statusFilter !== '' && { is_active: statusFilter }),
      }
      const res = await api.get('/plan/features/', { params })
      setResponse(res.data)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [filters])

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => ({ ...prev, page: 1 }))
    }, 1000)
    return () => clearTimeout(timer)
  }, [search, categoryFilter, statusFilter])

  return (
    <>
      <Helmet>
        <title>Funcionalidades - Admin</title>
      </Helmet>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" fontWeight="bold">
            Funcionalidades
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/admin/funcionalidades/create')}
          >
            Nova Funcionalidade
          </Button>
        </Box>

        <Card>
          <Box sx={{ p: 2, display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Pesquisar por nome..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              size="small"
              label="Categoria"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">Todas</MenuItem>
              <MenuItem value="MODULE">Módulo</MenuItem>
              <MenuItem value="FEATURE">Funcionalidade</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="true">Ativo</MenuItem>
              <MenuItem value="false">Inativo</MenuItem>
            </TextField>
          </Box>

          {response === null ? (
            <Skeleton variant="rounded" height={400} animation="pulse" sx={{ m: 2 }} />
          ) : response?.results?.length === 0 ? (
            <Box sx={{ p: 4 }}>
              <NoResultsAnimation />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Nome</TableCell>
                      <TableCell>Slug</TableCell>
                      <TableCell>Categoria</TableCell>
                      <TableCell>Módulo Pai</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {response?.results?.map((feature) => (
                      <TableRow
                        key={feature.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/admin/funcionalidades/${feature.id}`)}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Extension fontSize="small" color="primary" />
                            {feature.name}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                            {feature.slug}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={feature.category === 'MODULE' ? 'Módulo' : 'Funcionalidade'}
                            size="small"
                            color={feature.category === 'MODULE' ? 'primary' : 'secondary'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {feature.parent_name || '-'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={feature.is_active ? 'Ativo' : 'Inativo'}
                            size="small"
                            color={feature.is_active ? 'success' : 'default'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {response?.count > 0 && (
                <PaginationGlobal
                  state={response}
                  filters={filters}
                  setFilters={setFilters}
                />
              )}
            </>
          )}
        </Card>
      </Container>
    </>
  )
}

export default FeatureList
